import type { Identifier } from "ra-core";

import {
  classifyProspectAge,
  getElapsedHours,
  selectProspectInboxDeals,
} from "../deals/prospectInbox";
import {
  countPosiAnswers,
  isPosiComplete,
  type PosiAnswers,
} from "../deals/posi";
import {
  ALMIKIA_STAGE_VALUES,
  INBOX_STAGE,
  isAlmikiaStageValue,
  KANBAN_STAGES,
} from "../deals/stages";
import type { Deal, DealStage } from "../types";

/**
 * Almikia Epic E7, TASK-093 (funnel grouping generalized to any number of
 * stages by Epic EP, TASK-107): the pure (no fetching, no i18n) logic behind
 * the Líder BPO dashboard's lead-signal blocks -- the team funnel, "Por
 * vencer" and "POSI incompleto" -- kept separate from `LiderLeadSignals.tsx`
 * so the grouping/selection rules are unit-testable without rendering
 * anything. Every list here is read-only and never turns into a per-advisor
 * count or ranking (Epic E7 hard constraints, DOMAIN RULE (k)).
 *
 * QEP9 (TASK-101 notes): "Por vencer" and "POSI incompleto" keep their E2
 * stage lists (`selectProspectInboxDeals`'s Prospecto-only scope,
 * `BEFORE_SHOWING_STAGES` below) -- only the team funnel follows the
 * effective stage configuration.
 */

const CLOSED_STAGES = new Set(["venta_cerrada", "cerrado_perdido"]);

/** The funnel's default stage order when no effective stage list is passed (with the seeded configuration, or in a test). */
const DEFAULT_FUNNEL_STAGES: ReadonlyArray<Pick<DealStage, "value">> =
  ALMIKIA_STAGE_VALUES.map((value) => ({ value }));

/**
 * The 3 stages "before Showing" (ticket wording) -- a lead here still needs a
 * complete POSI to ever reach the POSI-gated stages (`posi.ts`).
 */
const BEFORE_SHOWING_STAGES = new Set<string>([
  INBOX_STAGE,
  ...KANBAN_STAGES.slice(0, KANBAN_STAGES.indexOf("showing")),
]);

/** Every non-archived, non-closed (not `venta_cerrada` / `cerrado_perdido`) team lead. */
export function selectOpenTeamLeads(deals: Deal[]): Deal[] {
  return deals.filter(
    (deal) => !deal.archived_at && !CLOSED_STAGES.has(deal.stage),
  );
}

export type LiderFunnelLead = {
  id: Identifier;
  name: string;
  stage: string;
  advisorName: string;
};

export type LiderFunnelStage = {
  stage: string;
  leads: LiderFunnelLead[];
};

/**
 * Groups every open team lead under its current stage, in `stages`' order
 * (the effective stage list, DATA CONTRACT EP (8): the 10 system stages plus
 * any direccion-created one -- defaults to the E2 list, `ALMIKIA_STAGE_VALUES`
 * when omitted, e.g. an existing caller/test); a stage with no open lead is
 * simply absent, never an empty placeholder group. A lead whose stage
 * matches none of `stages` (legacy/unknown, never a direccion-created stage
 * once TASK-107 wires the effective list in) is bucketed under `INBOX_STAGE`
 * instead of dropped -- the funnel lists EVERY open team lead (acceptance
 * criteria), matching `findDealLabel` / `selectProspectInboxDeals`'s own
 * fallback (never `partitionDealsByStages`'s drop, which is for the kanban
 * board only).
 */
export function buildTeamFunnel(
  deals: Deal[],
  advisorNameById: Map<Identifier, string>,
  stages: ReadonlyArray<Pick<DealStage, "value">> = DEFAULT_FUNNEL_STAGES,
): LiderFunnelStage[] {
  const openLeads = selectOpenTeamLeads(deals);
  const knownStageValues = new Set(stages.map((stage) => stage.value));
  const funnelStage = (deal: Deal): string =>
    knownStageValues.has(deal.stage) ? deal.stage : INBOX_STAGE;
  return stages
    .map(({ value: stage }) => ({
      stage,
      leads: openLeads
        .filter((deal) => funnelStage(deal) === stage)
        .map((deal) => ({
          id: deal.id,
          name: deal.name,
          stage: deal.stage,
          advisorName: advisorNameById.get(deal.sales_id) ?? "",
        })),
    }))
    .filter((group) => group.leads.length > 0);
}

export type LiderExpiringLead = {
  id: Identifier;
  name: string;
  advisorName: string;
  flag: "due_soon" | "overdue";
  hours: number;
};

/**
 * "Por vencer": team Prospecto leads about to breach (or already past) the
 * 24h SLA, overdue first (DOMAIN RULE (k): a commercial signal, never a
 * per-advisor count). Oldest first within each bucket, matching
 * `selectProspectInboxDeals`'s own ordering.
 */
export function selectTeamExpiringLeads(
  deals: Deal[],
  advisorNameById: Map<Identifier, string>,
  now: Date = new Date(),
): LiderExpiringLead[] {
  const openLeads = deals.filter((deal) => !deal.archived_at);
  const overdue: LiderExpiringLead[] = [];
  const dueSoon: LiderExpiringLead[] = [];
  for (const deal of selectProspectInboxDeals(openLeads)) {
    const flag = classifyProspectAge(deal.created_at, now);
    if (flag === "on_time") continue;
    const lead: LiderExpiringLead = {
      id: deal.id,
      name: deal.name,
      advisorName: advisorNameById.get(deal.sales_id) ?? "",
      flag,
      hours: getElapsedHours(deal.created_at, now),
    };
    (flag === "overdue" ? overdue : dueSoon).push(lead);
  }
  return [...overdue, ...dueSoon];
}

const POSI_FIELD_ORDER = [
  "posi_intent",
  "posi_budget",
  "posi_payment_method",
  "posi_decision_maker",
] as const;
export type PosiField = (typeof POSI_FIELD_ORDER)[number];

const isBlankPosiAnswer = (value: string | null | undefined): boolean =>
  value == null || value.trim() === "";

/** Every POSI field still unanswered, in canonical order (never just the first one). */
function missingPosiFields(answers: Partial<PosiAnswers>): PosiField[] {
  return POSI_FIELD_ORDER.filter((field) => isBlankPosiAnswer(answers[field]));
}

export type LiderPosiIncompleteLead = {
  id: Identifier;
  name: string;
  advisorName: string;
  answered: number;
  missingFields: PosiField[];
};

/**
 * "POSI incompleto": non-archived team leads still before Showing with an
 * incomplete POSI, oldest first -- naming every missing field (acceptance
 * criteria: "the missing answers named", plural) so the Líder BPO knows
 * exactly what is left, never just an "incomplete" flag.
 */
export function selectTeamPosiIncompleteLeads(
  deals: Deal[],
  advisorNameById: Map<Identifier, string>,
): LiderPosiIncompleteLead[] {
  return deals
    .filter(
      (deal) =>
        !deal.archived_at &&
        BEFORE_SHOWING_STAGES.has(
          isAlmikiaStageValue(deal.stage) ? deal.stage : INBOX_STAGE,
        ) &&
        !isPosiComplete({
          posi_intent: deal.posi_intent,
          posi_budget: deal.posi_budget,
          posi_payment_method: deal.posi_payment_method,
          posi_decision_maker: deal.posi_decision_maker,
        }),
    )
    .slice()
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    .map((deal) => {
      const answers: Partial<PosiAnswers> = {
        posi_intent: deal.posi_intent,
        posi_budget: deal.posi_budget,
        posi_payment_method: deal.posi_payment_method,
        posi_decision_maker: deal.posi_decision_maker,
      };
      return {
        id: deal.id,
        name: deal.name,
        advisorName: advisorNameById.get(deal.sales_id) ?? "",
        answered: countPosiAnswers(answers),
        missingFields: missingPosiFields(answers),
      };
    });
}
